import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Users } from 'lucide-react';
import { api } from '../services/api';

interface Militar {
  id: string;
  nick: string;
  patente?: string;
}

interface MilitarSelecionado {
  id: string;
  nick: string;
}

interface MilitaresMultiSelectFieldProps {
  /** Valor armazenado como JSON string: '[{"id":"...","nick":"..."}]' */
  value?: string;
  onChange: (jsonValue: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

function parseValor(value?: string): MilitarSelecionado[] {
  if (!value || value.trim() === '') return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

export function MilitaresMultiSelectField({
  value = '',
  onChange,
  placeholder = 'Pesquise e selecione militares...',
  disabled = false,
  required = false,
}: MilitaresMultiSelectFieldProps) {
  const [filtrados, setFiltrados] = useState<Militar[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selecionados = parseValor(value);

  // Busca na API com debounce ao digitar
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!newValue.trim()) {
      setFiltrados([]);
      setSearching(false);
      return;
    }

    setSearching(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const selecionadosIds = new Set(selecionados.map((s) => s.id));
        const response = await api.get(`/militares?nick=${encodeURIComponent(newValue)}`);
        const data = response.data;
        const lista: Militar[] = data.militares || data;
        setFiltrados(
          (Array.isArray(lista) ? lista : [])
            .filter((m) => !selecionadosIds.has(m.id))
            .slice(0, 10)
        );
      } catch {
        setFiltrados([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const adicionarMilitar = (militar: Militar) => {
    const novaLista: MilitarSelecionado[] = [
      ...selecionados,
      { id: militar.id, nick: militar.nick },
    ];
    onChange(JSON.stringify(novaLista));
    setSearchTerm('');
    setFiltrados([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const removerMilitar = (militarId: string) => {
    const novaLista = selecionados.filter((s) => s.id !== militarId);
    onChange(JSON.stringify(novaLista));
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Área de tags + input */}
      <div
        className={`min-h-[42px] w-full flex flex-wrap gap-1.5 items-center px-3 py-2 border rounded-md bg-background transition-all cursor-text
          ${disabled ? 'opacity-50 pointer-events-none' : ''}
          ${isOpen ? 'border-ring ring-2 ring-ring/20' : 'border-border'}
        `}
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        {/* Tags dos militares selecionados */}
        {selecionados.map((m) => (
          <span
            key={m.id}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs font-semibold px-2 py-0.5 max-w-[160px]"
          >
            <Users className="h-3 w-3 shrink-0" />
            <span className="truncate">{m.nick}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removerMilitar(m.id);
              }}
              className="ml-0.5 rounded-full hover:bg-primary/20 p-0.5 transition-colors"
              disabled={disabled}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}

        {/* Input de busca */}
        <div className="relative flex-1 min-w-[140px] flex items-center">
          <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-0 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={() => setIsOpen(true)}
            placeholder={selecionados.length === 0 ? placeholder : 'Adicionar mais...'}
            className="w-full pl-5 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            disabled={disabled}
            required={required && selecionados.length === 0}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg max-h-56 overflow-y-auto">
          {searching ? (
            <div className="px-4 py-3 text-sm text-muted-foreground text-center">
              Buscando...
            </div>
          ) : filtrados.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground text-center">
              {searchTerm.trim() ? 'Nenhum militar encontrado.' : 'Digite para buscar militares'}
            </div>
          ) : (
            filtrados.map((m) => (
              <button
                key={m.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  adicionarMilitar(m);
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors text-left"
              >
                <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium">{m.nick}</span>
                {m.patente && (
                  <span className="text-xs text-muted-foreground ml-auto">{m.patente}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}

      {/* Contador */}
      {selecionados.length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          {selecionados.length} militar{selecionados.length !== 1 ? 'es' : ''} selecionado{selecionados.length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
